import { mostReadable, TinyColor } from '@ctrl/tinycolor'
import * as RadixAvatar from '@radix-ui/react-avatar'
import { ComponentPropsWithoutRef } from 'react'

import { cn, resolveDevAssetUrl } from '@sparkai/utils'

import { Icon } from '../Icon'

export interface AvatarProps extends ComponentPropsWithoutRef<'div'> {
  imageProps?: ComponentPropsWithoutRef<'img'>
  fallbackProps?: ComponentPropsWithoutRef<'span'>
  color?: string
  fallback?: {
    delayMs?: number
    value: string
  }
  height?: number | string
  width?: number | string
  textProps?: ComponentPropsWithoutRef<'span'>
  badgeStatus?: {
    status: 'online' | 'idle' | 'offline'
  }
  divClassName?: string
  enableCloseIcon?: boolean
}

export function Avatar({
  color,
  fallback,
  imageProps,
  fallbackProps,
  height = 40,
  width = 40,
  badgeStatus,
  textProps,
  divClassName,
  enableCloseIcon,
  ...props
}: Readonly<AvatarProps>) {
  const bgColor = color ?? '#6B7280'
  const textColor = mostReadable(new TinyColor(bgColor), ['#FFFFFF', '#000000'])?.toRgbString() ?? '#000000'
  const imageSrc = imageProps?.src ? resolveDevAssetUrl(imageProps.src) : undefined
  const isSVG = imageSrc?.includes('.svg')

  const sanitizedFallback = fallback?.value?.trim()
  const initials = sanitizedFallback
    ?.split(/\s+/)
    // Remove non-word characters like punctuation and underscores
    .map((value) => value.replace(/\W|_/, ''))
    .filter(Boolean)
    .map((name) => name.slice(0, 1).toUpperCase())
    .slice(0, 2)
    .join('')

  return (
    <RadixAvatar.Root {...props}>
      <div
        className={cn('chromatic-ignore relative h-auto w-auto', divClassName)}
        style={{
          minHeight: height,
          minWidth: width,
        }}
      >
        <RadixAvatar.Image
          {...imageProps}
          className={cn(imageProps?.className, {
            'inline-block rounded-full': !isSVG,
            'flex items-center justify-center rounded-full p-2': isSVG,
          })}
          src={imageSrc}
          style={{
            backgroundColor: isSVG ? bgColor : '',
            width: imageSrc ? width : 0,
            height: imageSrc ? height : 0,
          }}
          crossOrigin='anonymous'
        />

        <RadixAvatar.Fallback
          {...fallbackProps}
          className={cn(
            'flex items-center justify-center rounded-full p-2 leading-none',
            fallbackProps?.className
          )}
          delayMs={fallback?.delayMs}
          style={{ backgroundColor: bgColor, color: textColor, width, height }}
        >
          <span {...textProps}>{initials}</span>
        </RadixAvatar.Fallback>
        {badgeStatus && (
          <>
            <div
              className={cn(
                'absolute right-[-2px] bottom-[-2px] h-[12px] w-[12px] rounded-md border-2 border-white',
                {
                  'bg-[#34D399]': badgeStatus.status === 'online',
                  'bg-[#D1D5DB]': badgeStatus.status === 'offline',
                }
              )}
            ></div>
            {badgeStatus.status === 'offline' && enableCloseIcon && (
              <div className='absolute right-[-5px] bottom-[-5px] h-[12px] w-[12px]'>
                <Icon name='close' className='h-[6px] w-[6px] text-gray-500' />
              </div>
            )}
          </>
        )}
      </div>
    </RadixAvatar.Root>
  )
}
